import { lazy, Suspense, useSyncExternalStore } from 'react'
import { reportError } from '../../lib/errorLog'
import { getSyncNotices, subscribeSyncNotices } from '../../lib/syncNotices'
import styles from './SyncNotices.module.css'

// The wording (the shared `describeNotice`) and the list load only when there is a notice.
// This sits above every error boundary, so a chunk that cannot load (offline, or a redeploy the
// stale-chunk reload gave up on) must not throw: it would unmount the whole app, editor included.
// The notices stay in memory and show after the next load. The failure is reported, not swallowed.
const SyncNoticeList = lazy(() =>
  import('./SyncNoticeList').catch((err: unknown) => {
    reportError(err, 'sync-notices chunk')
    return { default: () => <></> }
  }),
)

/**
 * QUIL: non-blocking sync notices (W11, app-platform design/W11-sync-orchestration.md §3.6).
 *
 * The quiet companion to the ConflictDialog: something sync did on the writer's behalf that
 * overrode something she did. An edit brought back a chapter or story another device had
 * deleted (decision 1, the edit wins), offering **Delete again**; a delete was refused because
 * another device had edited it since (decision 2), so it was kept; a chapter deleted on another
 * device was removed here too, with a copy in Recently deleted (ledger #9).
 *
 * A polite `role="status"` live region that is always mounted (so a notice added later is
 * announced), never a modal, and never dismissed by a timer: each stays until she closes it or
 * acts on it, which more than meets the shared "never before 10 s" rule.
 */
export function SyncNotices() {
  const notices = useSyncExternalStore(subscribeSyncNotices, getSyncNotices)
  return (
    <div className={styles.region} role="status" aria-live="polite" data-testid="sync-notices">
      {notices.length > 0 && (
        <Suspense fallback={null}>
          <SyncNoticeList notices={notices} />
        </Suspense>
      )}
    </div>
  )
}
